/**
 * Serviço de API para comunicação com o backend
 * Substitui as chamadas diretas ao Firebase/Gemini
 */

import { API_CONFIG } from '../../config/api';

// URL base do backend (remove /api/pareceres da webhook URL)
const BACKEND_URL = API_CONFIG.WEBHOOK_URL.replace('/api/pareceres', '');

// Helper para fazer requisições autenticadas
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${BACKEND_URL}${endpoint}`;

    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'X-API-Token': API_CONFIG.TOKEN,
            ...options.headers,
        },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Erro HTTP ${response.status}`);
    }

    // 204 No Content não tem corpo
    if (response.status === 204) {
        return null as T;
    }

    return response.json();
}

// ===== ANALYSES =====

export interface Analysis {
    id?: string;
    userId: string;
    fileName: string;
    roleId: string;
    analysis: string;
    ementa: string;
    fileUrl?: string;
    storagePath?: string;
    createdAt?: Date;
}

export async function saveAnalysis(analysis: Omit<Analysis, 'id'>): Promise<{ id: string }> {
    return request('/api/analyses', {
        method: 'POST',
        body: JSON.stringify(analysis),
    });
}

export async function getUserAnalyses(userId: string): Promise<Analysis[]> {
    return request(`/api/analyses/user/${userId}`);
}

export async function deleteAnalysis(id: string): Promise<void> {
    return request(`/api/analyses/${id}`, { method: 'DELETE' });
}

// ===== ROLES =====

export interface Role {
    id?: string;
    name: string;
    description?: string;
    userId?: string;
}

export async function saveRole(role: Omit<Role, 'id'>): Promise<{ id: string }> {
    return request('/api/roles', {
        method: 'POST',
        body: JSON.stringify(role),
    });
}

export async function getRoles(): Promise<Role[]> {
    return request('/api/roles');
}

export async function updateRole(id: string, role: Partial<Role>): Promise<void> {
    return request(`/api/roles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(role),
    });
}

export async function deleteRole(id: string): Promise<void> {
    return request(`/api/roles/${id}`, { method: 'DELETE' });
}

// ===== USER USAGE =====

export interface UsageData {
    daily: { date: string; count: number };
    monthly: { month: string; count: number };
}

export async function getUserUsage(userId: string): Promise<UsageData | null> {
    return request(`/api/users/${userId}/usage`);
}

export async function checkUsage(userId: string): Promise<{ allowed: boolean; message?: string }> {
    return request(`/api/users/${userId}/usage/check`);
}

export async function incrementUsage(userId: string, tokens: number): Promise<{ success: boolean }> {
    return request(`/api/users/${userId}/usage`, {
        method: 'POST',
        body: JSON.stringify({ tokens }),
    });
}

// ===== CHATS =====

export interface Chat {
    id?: string;
    userId: string;
    model: string;
    title?: string;
    tokenUsage?: number;
    isArchived?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ChatMessage {
    id?: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    createdAt?: Date;
}

export async function createChat(userId: string, model: string, title?: string): Promise<{ id: string }> {
    return request('/api/chats', {
        method: 'POST',
        body: JSON.stringify({ userId, model, title }),
    });
}

export async function getUserChats(userId: string): Promise<Chat[]> {
    return request(`/api/chats/user/${userId}`);
}

export async function getChatMessages(chatId: string): Promise<ChatMessage[]> {
    return request(`/api/chats/${chatId}/messages`);
}

export async function addMessage(chatId: string, message: Omit<ChatMessage, 'id' | 'createdAt'>): Promise<ChatMessage> {
    return request(`/api/chats/${chatId}/messages`, {
        method: 'POST',
        body: JSON.stringify(message),
    });
}

export async function updateChatTitle(chatId: string, title: string): Promise<void> {
    return request(`/api/chats/${chatId}/title`, {
        method: 'PUT',
        body: JSON.stringify({ title }),
    });
}

export async function archiveChat(chatId: string): Promise<void> {
    return request(`/api/chats/${chatId}/archive`, { method: 'PUT' });
}

export async function deleteChat(chatId: string): Promise<void> {
    return request(`/api/chats/${chatId}`, { method: 'DELETE' });
}

// ===== UPLOAD =====

export interface UploadResult {
    downloadUrl: string;
    storagePath: string;
}

export async function uploadFile(
    base64: string,
    fileName: string,
    userId: string,
    mimeType: string
): Promise<UploadResult> {
    return request('/api/upload', {
        method: 'POST',
        body: JSON.stringify({ base64, fileName, userId, mimeType }),
    });
}

// ===== GEMINI PRE-ANALYSIS =====

export interface PreAnalysisResult {
    sessionId: string;
    preAnalysis: string;
    fileUri: string;
}

export async function startPreAnalysis(
    base64: string,
    mimeType: string,
    roleName: string,
    matter: string,
    userAnswers?: string[]
): Promise<PreAnalysisResult> {
    return request('/api/gemini/pre-analyze', {
        method: 'POST',
        body: JSON.stringify({ base64, mimeType, roleName, matter, userAnswers }),
    });
}

export async function refinePreAnalysis(
    sessionId: string,
    feedback: string
): Promise<{ text: string }> {
    return request('/api/gemini/refine', {
        method: 'POST',
        body: JSON.stringify({ sessionId, feedback }),
    });
}

export async function endPreAnalysisSession(sessionId: string): Promise<void> {
    return request(`/api/gemini/session/${sessionId}`, { method: 'DELETE' });
}

// ===== CLARIFYING QUESTIONS =====

export interface ClarifyingQuestionsResult {
    questions: string[];
}

/**
 * Obtém as perguntas esclarecedoras pré-definidas.
 * Estas perguntas são sobre o OBJETIVO do usuário, não sobre o documento.
 */
export async function getClarifyingQuestions(): Promise<ClarifyingQuestionsResult> {
    return request('/api/gemini/clarifying-questions', {
        method: 'GET',
    });
}

export async function submitClarifyingAnswers(
    sessionId: string,
    answers: string[]
): Promise<{ success: boolean; answersStored: boolean }> {
    return request('/api/gemini/submit-answers', {
        method: 'POST',
        body: JSON.stringify({ sessionId, answers }),
    });
}


